import statistics from '../../common/helpers/statistics';
import filter from '../../common/helpers/filter';
import baseDataForm from '../../clients/detail/dndForm/baseDataForm';
import coreDataForm from '../../clients/detail/dndForm/coreDataForm';
import diagnosisDataForm from '../../clients/detail/dndForm/diagnosisDataForm';
import phoneDataForm from '../../clients/detail/dndForm/phoneDataForm';
import outcomeOfConsultationForm from '../../clients/detail/dndForm/outcomeOfConsultationForm';
import type { Client } from '../../common/types/client';
import type { DecryptedItem } from '../../common/types/items';
import type { StatisticsFilter } from '../../common/types/statistics/statistics';
import type {
  ClientStatistic,
  OutcomeOfConsultationStatistic,
  ClientsByAgeStatistic,
  Statistic,
} from '../../common/types/statistics/dndStatistics';
import type { Field } from '../../common/types/form';

const pieChartColors = ['#5F93C8', '#CB625E', '#AAC769', '#9372B0', '#5CB7CF'];

const clientOptionsKeyFieldsMapping: {
  key: keyof ClientStatistic;
  fields: Field<Client>[];
}[] = [
  {
    key: 'phoneCallDetails',
    fields: baseDataForm.fields,
  },
  {
    key: 'location',
    fields: coreDataForm.fields,
  },
  {
    key: 'age',
    fields: coreDataForm.fields,
  },
  {
    key: 'personType',
    fields: coreDataForm.fields,
  },
  {
    key: 'perdioOfEatingDisorder',
    fields: diagnosisDataForm.fields,
  },
  {
    key: 'eatingDisorder',
    fields: diagnosisDataForm.fields,
  },
  {
    key: 'measuresTakenSoFar',
    fields: phoneDataForm.fields,
  },
];

const getOutcomeOfConsultationStatistic = (clients: DecryptedItem<Client>[]): OutcomeOfConsultationStatistic => {
  const numberOfAppointmentForCounselling = statistics.getNumberOfValues(clients, 'appointmentForCounselling');
  const numberOfPsychosocialCounselling = statistics.getNumberOfValues(clients, 'psychosocialCounselling');
  const numberOfCrisisintervention = statistics.getNumberOfValues(clients, 'crisisintervention');
  const numberOfNutritionCounselling = statistics.getNumberOfValues(clients, 'nutritionCounselling');

  const appointmentTreatmentOptions = statistics.getStatisticByOptions(
    clients,
    'appointmentTreatmentOptions',
    outcomeOfConsultationForm.fields
  );

  const informationTopics = statistics.getStringValueAsArray(clients, 'informationTopic');
  const otherTopics = statistics.getStringValueAsArray(clients, 'otherTopic');

  return {
    numberOfAppointmentForCounselling,
    appointmentTreatmentOptions,
    numberOfPsychosocialCounselling,
    numberOfCrisisintervention,
    numberOfNutritionCounselling,
    informationTopics,
    otherTopics,
  };
};

const ageSplit = [
  'eighteenTillTwentyone',
  'twentytwoTillThirty',
  'thirtyoneTillFourtyfive',
  'fourtysixTillFivtyfive',
  'overFivtysix',
];

const getClientsByAgeStatistic = (clients: DecryptedItem<Client>[], overEighteen: boolean): ClientsByAgeStatistic => {
  const clientStatistic = clientOptionsKeyFieldsMapping.reduce(
    (optionStatistic, { key, fields }) => {
      const options = statistics.getStatisticByOptions(clients, key, fields);
      if (key === 'age') {
        const ageOptions = Object.keys(options)
          .filter((optionKey) => {
            if (optionKey === 'notSpecified') {
              return true;
            }
            if (overEighteen) {
              return ageSplit.includes(optionKey);
            }
            return !ageSplit.includes(optionKey);
          })
          .reduce(
            (newOptions, optionKey) => ({
              ...newOptions,
              [optionKey]: options[optionKey],
            }),
            {}
          );
        return {
          ...optionStatistic,
          [key]: ageOptions,
        };
      }
      return {
        ...optionStatistic,
        [key]: options,
      };
    },
    {
      phoneCallDetails: {},
      location: {},
      age: {},
      personType: {},
      perdioOfEatingDisorder: {},
      eatingDisorder: {},
      measuresTakenSoFar: {},
      outcomeOfConsultation: {},
    }
  );

  const outcomeOfConsultationStatistic = getOutcomeOfConsultationStatistic(clients);

  return { numberOfClients: clients.length, clientStatistic, outcomeOfConsultationStatistic };
};

type ClientsByAge = {
  clientsUnderEighteen: DecryptedItem<Client>[];
  clientsOverEighteen: DecryptedItem<Client>[];
};

const filterClients = (clients: DecryptedItem<Client>[], statisticFilter: StatisticsFilter): ClientsByAge => {
  return clients.reduce(
    (prevValue: ClientsByAge, client: DecryptedItem<Client>) => {
      const isInDateRange = filter.isInYear(client.payload.date, statisticFilter.year);

      if (isInDateRange) {
        if (client.payload.isUnderEighteen) {
          return {
            clientsUnderEighteen: prevValue.clientsUnderEighteen.concat([client]),
            clientsOverEighteen: prevValue.clientsOverEighteen,
          };
        }
        return {
          clientsUnderEighteen: prevValue.clientsUnderEighteen,
          clientsOverEighteen: prevValue.clientsOverEighteen.concat([client]),
        };
      }
      return prevValue;
    },
    { clientsUnderEighteen: [], clientsOverEighteen: [] }
  );
};

const createStatistics = (clients: DecryptedItem<Client>[], statisticFilter: StatisticsFilter): Statistic => {
  const { clientsUnderEighteen, clientsOverEighteen } = filterClients(clients, statisticFilter);

  const numberOfPhoneConsultation =
    statistics.getNumberOfValues(clientsUnderEighteen, 'contact', [
      'duringPhoneConsultationHour',
      'duringGeneralSpeakingTime',
      'voiceMailAndCallback',
    ]) +
    statistics.getNumberOfValues(clientsOverEighteen, 'contact', [
      'duringPhoneConsultationHour',
      'duringGeneralSpeakingTime',
      'voiceMailAndCallback',
    ]);
  const numberOfMailContacts =
    statistics.getNumberOfValues(clientsUnderEighteen, 'contact', ['byWriting']) +
    statistics.getNumberOfValues(clientsOverEighteen, 'contact', ['byWriting']);

  const statisticUnderEighteen = getClientsByAgeStatistic(clientsUnderEighteen, false);
  const statisticOverEighteen = getClientsByAgeStatistic(clientsOverEighteen, true);

  return {
    numberOfPhoneConsultation,
    numberOfMailContacts,
    statisticUnderEighteen,
    statisticOverEighteen,
  };
};

export { clientOptionsKeyFieldsMapping, pieChartColors };

export default createStatistics;
